import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, mixins } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import styled, { css } from "styled-components";

import {
    HIGHLIGHT_ANIMATION_BACKGROUND_COLOR,
    HIGHLIGHT_ANIMATION_TIME,
} from "app/pages/sales-intelligence/constants/highlight-animation";
import { ACCIDENTAL_ACTION_DEBOUNCE_TIME } from "app/pages/sales-intelligence/constants/timeouts";
import { SignalsLoader } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsLoader/SignalsLoader";

import {
    StyledSignalSelectionTooltipInlineTrigger,
    StyledSignalSelectionTooltipTrigger,
} from "../SignalSelectionTooltipTrigger/styles";
import { StyledSignalName } from "../styles";

const SIGNAL_INNER_HEIGHT = "20px";

export enum BorderVariant {
    Default,
    Selected,
    Active,
}

const colorByVariant = {
    [BorderVariant.Default]: colorsPalettes.carbon["50"],
    [BorderVariant.Selected]: colorsPalettes.blue["200"],
    [BorderVariant.Active]: colorsPalettes.carbon["300"],
};

const StyledRow = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
`;

const StyledButton = styled(IconButton)`
    & ${SWReactIcons} {
        height: 16px;
        width: 16px;

        & svg path {
            fill-opacity: 1;
        }
    }
`;

const StyledButtonWrap = styled.div`
    flex-shrink: 0;

    &:not(:first-child) {
        margin-left: 4px;
    }
`;

export const StyledLoader = styled(SignalsLoader)`
    display: inline-flex;
    align-self: stretch;
    max-height: ${SIGNAL_INNER_HEIGHT};

    ${DotsLoader} {
        align-self: center;
    }
`;

export const StyledSignalNameText = styled(StyledSignalName)`
    ${mixins.truncateText()}
`;

export const StyledSignalNameWrapper = styled.div`
    flex: 1 1 0;
    min-width: 0;
    ${mixins.truncateText()}
`;

export const StyledSignalNameBadgeWrapper = styled.span`
    margin-left: 8px;
    flex-shrink: 0;
    white-space: nowrap;
`;

export const StyledTopRow = styled(StyledRow)`
    align-items: flex-start;
`;

export const StyledBottomRow = styled(StyledRow)`
    margin-top: 4px;
`;

export const StyledSignalInner = styled.div`
    max-height: ${SIGNAL_INNER_HEIGHT};
    overflow-y: hidden;
    transition: max-height 450ms ease-in-out;
    transition-delay: ${ACCIDENTAL_ACTION_DEBOUNCE_TIME}ms;
`;

export const StyledSignal = styled.div<{ isSelected?: boolean; isHighlighted?: boolean }>`
    background-color: transparent;
    border-radius: 8px;
    box-sizing: border-box;
    cursor: ${({ onClick }) => (typeof onClick === "undefined" ? "default" : "pointer")};
    padding: 16px;
    transition: background-color ${HIGHLIGHT_ANIMATION_TIME}ms ease-in;

    ${({ isHighlighted }) =>
        isHighlighted &&
        css`
            background-color: ${HIGHLIGHT_ANIMATION_BACKGROUND_COLOR};
        `}

    ${({ onClick, isSelected }) => {
        if (isSelected) {
            return css`
                outline: 1px solid ${colorByVariant[BorderVariant.Selected]};
            `;
        }

        if (typeof onClick === "undefined") {
            return css`
                &:hover {
                    outline: 1px solid ${colorByVariant[BorderVariant.Active]};
                }
            `;
        }

        return css`
            outline: 1px solid ${colorByVariant[BorderVariant.Default]};
        `;
    }};

    &:hover {
        & ${StyledSignalInner} {
            max-height: 200px;
        }

        & ${StyledSignalNameWrapper} {
            overflow: visible;
            text-overflow: clip;
            white-space: normal;
        }

        & ${StyledSignalNameText} {
            white-space: normal;
        }

        & ${StyledSignalSelectionTooltipTrigger}, & ${StyledSignalSelectionTooltipInlineTrigger} {
            overflow: visible;
            text-overflow: clip;
            white-space: normal;
        }
    }

    &:not(:hover) ${StyledSignalSelectionTooltipTrigger}, &:not(:hover) ${StyledSignalSelectionTooltipInlineTrigger} {
        max-width: 100%;
        ${mixins.truncateText()}
    }
`;
